import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
  limit,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { runTransaction } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { BASE_PCGE } from "./pcgeDefaults";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Extracts YYYY-MM period from a date string (YYYY-MM-DD or similar).
 */
export const getPeriodoFromFecha = (fecha) => {
  const f = cleanText(fecha);
  if (f.length >= 7) return f.slice(0, 7);
  return f;
};

/**
 * Helper to check if a specific period/type is closed in gco_sire_cierres.
 */
const isPeriodoCerrado = async (rucEmpresa, periodo, tipo) => {
  const ruc = cleanText(rucEmpresa);
  const per = cleanText(periodo);
  const tip = cleanText(tipo).toUpperCase();
  if (!ruc || !per || !tip) return false;

  const docId = `${ruc}_${per}_${tip}`;
  const docRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, docId);
  const snap = await getDoc(docRef);

  if (snap.exists()) {
    const data = snap.data();
    return cleanText(data.estado).toUpperCase() === "CERRADO";
  }
  return false;
};

/**
 * Asserts that the period corresponding to the given date is open.
 * Throws an error if the period is locked/closed.
 */
export const assertPeriodoAbierto = async (rucEmpresa, fecha) => {
  const ruc = cleanText(rucEmpresa);
  const periodo = getPeriodoFromFecha(fecha);
  if (!ruc || !periodo) {
    throw new Error("RUC de empresa y fecha son obligatorios para validar el periodo.");
  }

  // Check RVIE, RCE and GENERAL closures. If any of them is closed, the period is locked.
  const [rvieClosed, rceClosed, generalClosed] = await Promise.all([
    isPeriodoCerrado(ruc, periodo, "RVIE"),
    isPeriodoCerrado(ruc, periodo, "RCE"),
    isPeriodoCerrado(ruc, periodo, "GENERAL"),
  ]);

  if (rvieClosed || rceClosed || generalClosed) {
    throw new Error(`Operación denegada: El período contable ${periodo} está CERRADO.`);
  }

  return true;
};

/**
 * Asserts that a period is open inside a Firestore transaction.
 */
export const assertPeriodoAbiertoTx = async (transaction, rucEmpresa, fecha) => {
  const ruc = cleanText(rucEmpresa);
  const periodo = getPeriodoFromFecha(fecha);
  if (!ruc || !periodo) {
    throw new Error("RUC de empresa y fecha son obligatorios para validar el periodo.");
  }

  const rvieId = `${ruc}_${periodo}_RVIE`;
  const rceId = `${ruc}_${periodo}_RCE`;

  const rvieRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, rvieId);
  const rceRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, rceId);
  const generalRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, `${ruc}_${periodo}_GENERAL`);

  const [rvieSnap, rceSnap, generalSnap] = await Promise.all([
    transaction.get(rvieRef),
    transaction.get(rceRef),
    transaction.get(generalRef),
  ]);

  const rvieClosed = rvieSnap.exists() && cleanText(rvieSnap.data()?.estado).toUpperCase() === "CERRADO";
  const rceClosed = rceSnap.exists() && cleanText(rceSnap.data()?.estado).toUpperCase() === "CERRADO";
  const generalClosed = generalSnap.exists() && cleanText(generalSnap.data()?.estado).toUpperCase() === "CERRADO";

  if (rvieClosed || rceClosed || generalClosed) {
    throw new Error(`Operación denegada: El período contable ${periodo} está CERRADO.`);
  }

  return true;
};

/**
 * Retrieves an account definition from the Plan de Cuentas (PCGE).
 * If the account does not exist in the company's custom accounts but exists in BASE_PCGE,
 * it is automatically provisioned with estado: 'ACTIVO' for zero operational friction.
 */
export const obtenerCuenta = async (rucEmpresa, cuentaCodigo) => {
  const ruc = cleanText(rucEmpresa);
  const codigo = cleanText(cuentaCodigo).replace(/[^\d]/g, "");
  if (!ruc || !codigo) return null;

  const id = `${ruc}_${codigo}`;
  const docRef = doc(db, CONT_COLLECTIONS.PLAN_CUENTAS, id);
  const snap = await getDoc(docRef);

  if (snap && typeof snap.exists === "function" && snap.exists()) {
    return { id: snap.id, ...snap.data() };
  }

  // Fallback PCGE Base: si la cuenta no ha sido registrada aún para la empresa,
  // la auto-aprovisionamos desde el catálogo oficial base.
  const baseAccount = BASE_PCGE.find((c) => cleanText(c.codigo).replace(/[^\d]/g, "") === codigo);
  if (baseAccount) {
    const accountData = {
      ruc_empresa: ruc,
      codigo: baseAccount.codigo,
      nombre: baseAccount.nombre,
      clase: baseAccount.clase,
      naturaleza: baseAccount.naturaleza,
      nivel: String(baseAccount.codigo.length),
      estado: "ACTIVO",
      origen: "PCGE_BASE_AUTO",
    };
    try {
      await setDoc(
        docRef,
        {
          ...accountData,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.warn(`No se pudo auto-inicializar la cuenta base ${codigo} para la empresa ${ruc}:`, err);
    }
    return { id, ...accountData };
  }

  return null;
};

/**
 * Validates that an account exists and is ACTIVE.
 */
export const validarCuentaActiva = async (rucEmpresa, cuentaCodigo) => {
  const cuenta = await obtenerCuenta(rucEmpresa, cuentaCodigo);
  if (!cuenta) {
    console.warn(`Cuenta contable ${cuentaCodigo} no encontrada para la empresa ${rucEmpresa}.`);
    return false;
  }
  return cleanText(cuenta.estado).toUpperCase() === "ACTIVO";
};

/**
 * Checks if the double-entry equation holds: SUM(debe) === SUM(haber).
 */
export const validarPartidaDoble = (debe = [], haber = []) => {
  const sumDebe = debe.reduce((acc, x) => acc + toNum(x.monto), 0);
  const sumHaber = haber.reduce((acc, x) => acc + toNum(x.monto), 0);
  return Math.abs(sumDebe - sumHaber) < 0.01;
};

/**
 * Central orchestrator to register a journal entry (asiento) inside a transaction.
 * Guarantees period checks, double-entry validation, idempotency mapping, and CUO generation.
 */
export const registrarAsientoCore = async (transaction, {
  rucEmpresa,
  asientoId,
  payload,
  idempotencyKey,
  metadata = {},
}) => {
  const ruc = cleanText(rucEmpresa);
  const id = cleanText(asientoId);
  if (!ruc || !id || !payload) {
    throw new Error("Datos incompletos para registrar el asiento contable.");
  }

  // 1. Validate period lock inside the transaction
  await assertPeriodoAbiertoTx(transaction, ruc, payload.fecha);

  // 2. Validate double entry
  if (!validarPartidaDoble(payload.debe, payload.haber)) {
    throw new Error(`Inconsistencia contable: El asiento ${id} no balancea (Debe: ${payload.total_debe || 0}, Haber: ${payload.total_haber || 0}).`);
  }

  // 3. Handle Idempotency
  if (idempotencyKey) {
    const key = cleanText(idempotencyKey);
    if (!key.startsWith(ruc)) {
      throw new Error(`Seguridad: La clave de idempotencia no corresponde al RUC de la empresa.`);
    }

    const idemRef = doc(db, CONT_COLLECTIONS.IDEMPOTENCIA, key);
    const idemSnap = await transaction.get(idemRef);

    if (idemSnap.exists()) {
      const idemData = idemSnap.data();
      const payloadTotal = toNum(payload.total_debe || payload.total_haber || 0);
      const originalTotal = toNum(idemData.total);
      const originalCurrency = cleanText(idemData.moneda || "PEN");
      const currentCurrency = cleanText(payload.moneda || "PEN");

      if (Math.abs(payloadTotal - originalTotal) > 0.01 || originalCurrency.toUpperCase() !== currentCurrency.toUpperCase()) {
        throw new Error(`Conflicto de idempotencia: Re-ejecución detectada con payload inconsistente para la clave ${key}.`);
      }

      return idemData.asiento_id;
    }
  }

  // 4. Generate CUO sequential per RUC + Period
  const periodo = getPeriodoFromFecha(payload.fecha);
  const cuoCorrelativoId = `${ruc}_${periodo}`;
  const cuoRef = doc(db, CONT_COLLECTIONS.CORRELATIVOS_CUO, cuoCorrelativoId);
  const cuoSnap = await transaction.get(cuoRef);

  let ultimoCuo = 0;
  if (cuoSnap.exists()) {
    ultimoCuo = toNum(cuoSnap.data().ultimo_cuo);
  }

  const nuevoCuoNum = ultimoCuo + 1;
  const nuevoCuo = String(nuevoCuoNum).padStart(6, "0");

  transaction.set(cuoRef, { ultimo_cuo: nuevoCuoNum }, { merge: true });

  // 5. Build final payload with CUO, idempotency, and audit metadata
  const finalPayload = {
    ...payload,
    ruc_empresa: ruc,
    cuo: nuevoCuo,
    idempotency_key: idempotencyKey || "",
    source_module: cleanText(metadata.sourceModule || payload.source_module || ""),
    source_type: cleanText(metadata.sourceType || payload.source_type || ""),
    source_id: cleanText(metadata.sourceId || payload.source_id || ""),
    source_document_id: cleanText(metadata.sourceDocumentId || payload.source_document_id || ""),
    created_by: cleanText(metadata.createdBy || payload.created_by || ""),
    created_at: payload.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  // 6. Write the seat
  const docRef = doc(db, CONT_COLLECTIONS.ASIENTOS, id);
  transaction.set(docRef, finalPayload, { merge: true });

  // 7. Save idempotency reference
  if (idempotencyKey) {
    const key = cleanText(idempotencyKey);
    const idemRef = doc(db, CONT_COLLECTIONS.IDEMPOTENCIA, key);
    transaction.set(idemRef, {
      ruc_empresa: ruc,
      asiento_id: id,
      cuo: nuevoCuo,
      total: toNum(payload.total_debe || payload.total_haber || 0),
      moneda: cleanText(payload.moneda || "PEN"),
      created_at: new Date().toISOString(),
    });
  }

  return id;
};

/**
 * Phase 1: Prepare data for batch journal entries.
 * ALL reads happen here (outside any Firestore transaction).
 * Returns an array of prepared items ready for commitAsientosCore.
 */
export const prepareAsientosCore = async ({
  rucEmpresa,
  asientos, // [{ asientoId, payload, idempotencyKey, metadata }]
}) => {
  const ruc = cleanText(rucEmpresa);
  const periodo = asientos.length > 0
    ? getPeriodoFromFecha(asientos[0].payload.fecha)
    : "";

  // 1. Validate period lock (all reads outside transaction)
  await assertPeriodoAbierto(ruc, asientos[0].payload.fecha);

  // 2. Read all idempotency docs
  const idemDocs = {};
  const idemKeys = asientos
    .map((a) => a.idempotencyKey)
    .filter(Boolean)
    .map((k) => cleanText(k));

  // Batch read idempotency docs
  const idemRefs = idemKeys.map((key) =>
    doc(db, CONT_COLLECTIONS.IDEMPOTENCIA, key)
  );
  const idemSnaps = await Promise.all(idemRefs.map((ref) => getDoc(ref)));
  idemKeys.forEach((key, i) => {
    idemDocs[key] = idemSnaps[i];
  });

  // 3. Read CUO correlativo doc
  const cuoCorrelativoId = `${ruc}_${periodo}`;
  const cuoRef = doc(db, CONT_COLLECTIONS.CORRELATIVOS_CUO, cuoCorrelativoId);
  const cuoSnap = await getDoc(cuoRef);
  let ultimoCuo = cuoSnap.exists() ? toNum(cuoSnap.data().ultimo_cuo) : 0;

  // 4. Validate each asiento: double entry + idempotency
  const prepared = [];
  for (const item of asientos) {
    const { asientoId, payload, idempotencyKey, metadata = {} } = item;
    const id = cleanText(asientoId);
    if (!id || !payload) throw new Error("Datos incompletos para registrar el asiento contable.");

    if (!validarPartidaDoble(payload.debe, payload.haber)) {
      throw new Error(`Inconsistencia contable: El asiento ${id} no balancea (Debe: ${payload.total_debe || 0}, Haber: ${payload.total_haber || 0}).`);
    }

    // Check idempotency (already read)
    if (idempotencyKey) {
      const key = cleanText(idempotencyKey);
      const idemSnap = idemDocs[key];
      if (idemSnap && idemSnap.exists()) {
        const idemData = idemSnap.data();
        const payloadTotal = toNum(payload.total_debe || payload.total_haber || 0);
        const originalTotal = toNum(idemData.total);
        const originalCurrency = cleanText(idemData.moneda || "PEN");
        const currentCurrency = cleanText(payload.moneda || "PEN");
        if (Math.abs(payloadTotal - originalTotal) > 0.01 || originalCurrency.toUpperCase() !== currentCurrency.toUpperCase()) {
          throw new Error(`Conflicto de idempotencia: Re-ejecución detectada con payload inconsistente para la clave ${key}.`);
        }
        // Already registered — skip this asiento
        continue;
      }
    }

    ultimoCuo += 1;
    const nuevoCuo = String(ultimoCuo).padStart(6, "0");
    const finalPayload = {
      ...payload,
      ruc_empresa: ruc,
      cuo: nuevoCuo,
      idempotency_key: idempotencyKey || "",
      source_module: cleanText(metadata.sourceModule || payload.source_module || ""),
      source_type: cleanText(metadata.sourceType || payload.source_type || ""),
      source_id: cleanText(metadata.sourceId || payload.source_id || ""),
      source_document_id: cleanText(metadata.sourceDocumentId || payload.source_document_id || ""),
      created_by: cleanText(metadata.createdBy || payload.created_by || ""),
      created_at: payload.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    prepared.push({
      asientoId: id,
      payload: finalPayload,
      idempotencyKey: idempotencyKey || "",
      cuoRef,
      cuoValue: ultimoCuo,
    });
  }

  return { prepared, cuoRef, cuoValue: ultimoCuo };
};

/**
 * Phase 2: Commit prepared journal entries atomically.
 * ALL writes happen here inside a single Firestore transaction.
 * Requires the output of prepareAsientosCore.
 */
export const commitAsientosCore = async ({ prepared, cuoRef, cuoValue }) => {
  if (!prepared || prepared.length === 0) return [];
  const committed = [];

  await runTransaction(db, async (transaction) => {
    // Atomic CUO update
    transaction.set(cuoRef, { ultimo_cuo: cuoValue }, { merge: true });

    for (const item of prepared) {
      // Write the asiento
      const docRef = doc(db, CONT_COLLECTIONS.ASIENTOS, item.asientoId);
      transaction.set(docRef, item.payload, { merge: true });

      // Write idempotency reference
      if (item.idempotencyKey) {
        const key = cleanText(item.idempotencyKey);
        const idemRef = doc(db, CONT_COLLECTIONS.IDEMPOTENCIA, key);
        transaction.set(idemRef, {
          ruc_empresa: cleanText(item.payload.ruc_empresa),
          asiento_id: item.asientoId,
          cuo: item.payload.cuo,
          total: toNum(item.payload.total_debe || item.payload.total_haber || 0),
          moneda: cleanText(item.payload.moneda || "PEN"),
          created_at: new Date().toISOString(),
        });
      }

      committed.push(item.asientoId);
    }
  });

  return committed;
};
